import React, { useState, useEffect } from 'react';
import apiService from '../../../services/api';
import { useNotification } from '../../../contexts/NotificationContext';
import SkeletonLoader from '../../UI/SkeletonLoader';
import EmptyState from '../../UI/EmptyState';
import styles from '../../../styles/TaskManagement.module.css';
import taskDetailsStyles from '../../../styles/TaskDetails.module.css';
import { useNavigate } from 'react-router-dom';
import { isOverdue, formatDate as formatDateHelper } from '../TaskManagement/components/helpers';

interface TaskInstance {
  id: string;
  name: string;
  description: string;
  status: string;
  priority: string;
  created_by: {
    id: string;
    full_name: string;
    email: string;
  };
  created_at: string;
  due_date: string | null;
  assignees: Array<{
    user: {
      id: string;
      full_name: string;
      email: string;
    };
    status: string;
  }>;
  group: {
    id: string;
    name: string;
  } | null;
}

interface TaskInstancesProps {
  templateId: string;
  groupId?: string | null;
  onInstanceClick?: (instanceId: string, groupId: string | null) => void;
}

const TaskInstances: React.FC<TaskInstancesProps> = ({ templateId, groupId, onInstanceClick }) => {
  const [instances, setInstances] = useState<TaskInstance[]>([]);
  const [loading, setLoading] = useState(true);
  const notificationService = useNotification();
  const navigate = useNavigate();

  useEffect(() => {
    loadInstances();
  }, [templateId, groupId]);

  const loadInstances = async () => {
    setLoading(true);
    try {
      // Use the new template instances API
      const params: any = {};
      if (groupId) {
        params.group_id = groupId;
      }

      const response = await apiService.getTemplateInstances(templateId, params);

      console.log('Template instances API response:', response);

      if (response.status === 'success' && response.data) {
        // Map the API response to the component's TaskInstance interface
        const instancesData = response.data.instances || [];
        console.log('Instances data from API:', instancesData);

        const mappedInstances = instancesData.map((instance: any) => {
          // Map assignees from backend format to frontend format
          const mappedAssignees = (instance.assignees || []).map((assignee: any) => ({
            user: {
              id: assignee.user_id || assignee.user?.id || '',
              full_name: assignee.user_name || assignee.user?.full_name || assignee.user?.name || 'Unknown',
              email: assignee.user_email || assignee.user?.email || '',
            },
            status: assignee.status || 'pending',
          }));

          return {
            id: instance.id,
            name: instance.name,
            description: instance.description,
            status: instance.status,
            priority: instance.priority,
            created_by: {
              id: instance.created_by?.id || '',
              full_name: instance.created_by?.name || instance.created_by?.full_name || 'Unknown',
              email: instance.created_by?.email || '',
            },
            created_at: instance.created_at,
            due_date: instance.due_date,
            assignees: mappedAssignees,
            group: instance.group,
          };
        });

        console.log('Mapped instances:', mappedInstances);
        setInstances(mappedInstances);
      } else {
        console.error('Failed to load instances:', response.message || 'Unknown error');
        notificationService.error(response.message || 'Failed to load task instances');
        setInstances([]);
      }
    } catch (error: any) {
      console.error('Error loading instances:', error);
      notificationService.error(error.response?.data?.message || error.response?.data?.error || 'Failed to load task instances');
      setInstances([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInstanceClick = (instance: TaskInstance, e?: React.MouseEvent) => {
    // Prevent any default behavior and stop propagation if event is provided
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Validate instance data
    if (!instance || !instance.id) {
      console.error('Invalid instance data:', instance);
      notificationService.error('Invalid task instance data');
      return;
    }

    try {
      // Always navigate to task-management route with instance ID
      const path = `/task-management/${instance.id}`;
      console.log('Navigating to instance:', path);
      if (onInstanceClick) {
        onInstanceClick(instance.id, null);
      }
      navigate(path);
    } catch (error: any) {
      console.error('Error navigating to task instance:', error);
      notificationService.error(error.message || 'Failed to navigate to task instance');
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'No due date';
    return formatDateHelper(dateString);
  };

  const formatPriority = (priority: string) => {
    return priority.charAt(0).toUpperCase() + priority.slice(1);
  };

  const getPriorityClass = (priority: string) => {
    const formatted = formatPriority(priority);
    return `priority${formatted}`;
  };

  const formatStatus = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const getStatusClass = (status: string) => {
    return `status${status
      .split('_')
      .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('')}`;
  };

  const getDaysUntilDue = (dueDate: string | null): string => {
    if (!dueDate) return '';
    const due = new Date(dueDate);
    const now = new Date();
    const diffTime = due.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) return 'Overdue';
    if (diffDays === 0) return 'Due Today';
    if (diffDays === 1) return '1 day';
    return `${diffDays} days`;
  };

  if (loading) {
    return (
      <div className={taskDetailsStyles.tabContent}>
        <SkeletonLoader count={5} height="100px" />
      </div>
    );
  }

  if (instances.length === 0) {
    return (
      <div className={taskDetailsStyles.tabContent}>
        <EmptyState
          title="No Instances Created"
          description="No task instances have been created from this template yet. Users assigned to this template can create instances from it."
        />
      </div>
    );
  }

  return (
    <div className={taskDetailsStyles.tabContent}>
      <h3 style={{ marginBottom: '1.5rem', fontSize: '1.25rem', fontWeight: '700', color: 'var(--primary-dark)' }}>
        Task Instances ({instances.length})
      </h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {instances.map((instance) => {
          const statusClass = getStatusClass(instance.status);
          const priorityClass = getPriorityClass(instance.priority);
          const description = instance.description || 'No description provided';

          return (
            <div
              key={instance.id}
              className={styles.taskCard}
              onClick={() => handleInstanceClick(instance)}
            >
              {/* Card Header */}
              <div className={styles.cardHeader}>
                <div className={styles.cardHeaderLeft}>
                  <div
                    className={styles.taskIconWrapper}
                    style={{ backgroundColor: '#3b82f620' }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                  </div>
                  <div className={styles.cardTitleSection}>
                    <h3 className={styles.cardTitle}>{instance.name}</h3>
                    <div className={styles.cardDescription}>
                      {description.length > 100 ? `${description.substring(0, 100)}...` : description}
                    </div>
                  </div>
                </div>
                <div className={styles.cardHeaderRight}>
                  <div className={`${styles.taskStatusBadge} ${styles[statusClass]}`}>
                    {formatStatus(instance.status)}
                  </div>
                </div>
              </div>

              {/* Card Body - Tags and Badges */}
              <div className={styles.cardBody}>
                <div className={styles.cardTags}>
                  {/* Instance Badge */}
                  <div
                    className={styles.categoryTag}
                    style={{
                      backgroundColor: '#3b82f615',
                      color: '#2563eb',
                      borderColor: '#3b82f640'
                    }}
                  >
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                    </svg>
                    <span style={{ fontSize: '0.625rem', fontWeight: '500' }}>Instance</span>
                  </div>

                  {/* Priority Tag */}
                  <div className={`${styles.priorityTag} ${styles[priorityClass]}`}>
                    <span style={{ fontSize: '0.625rem' }}>{formatPriority(instance.priority)} Priority</span>
                  </div>

                  {/* Due Date Tag */}
                  {instance.due_date && (
                    <div className={`${styles.deadlineTag} ${isOverdue(instance.due_date) ? styles.overdue : ''}`}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                      </svg>
                      <span style={{ fontSize: '0.625rem' }}>{getDaysUntilDue(instance.due_date)}</span>
                    </div>
                  )}

                  {/* Group Tag */}
                  {instance.group && instance.group.name && (
                    <div className={styles.groupTag}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '0.375rem' }}>
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <span style={{ fontSize: '0.625rem' }}>{instance.group.name}</span>
                    </div>
                  )}
                </div>

                {/* Metadata Section */}
                <div className={styles.cardMetadata}>
                  {instance.assignees && instance.assignees.length > 0 && (
                    <div className={styles.metadataItem}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                        <circle cx="9" cy="7" r="4"></circle>
                        <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                        <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                      </svg>
                      <span className={styles.metadataText}>
                        {instance.assignees.length} Assignee{instance.assignees.length !== 1 ? 's' : ''}
                      </span>
                    </div>
                  )}

                  <div className={styles.metadataItem}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                      <line x1="16" y1="2" x2="16" y2="6"></line>
                      <line x1="8" y1="2" x2="8" y2="6"></line>
                      <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                    <span className={styles.metadataText}>Created: {formatDateHelper(instance.created_at)}</span>
                  </div>

                  <div className={styles.metadataItem}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.metadataIcon}>
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                      <circle cx="12" cy="7" r="4"></circle>
                    </svg>
                    <span className={styles.metadataText}>By: {instance.created_by.full_name}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TaskInstances;

